/** A single map vertex as a `[latitude, longitude]` tuple. */
export type PolygonPoint = [number, number];

const asFinite = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** API vertices arrive as `{ lat, lng }` (or aliases); the map uses `[lat, lng]` tuples. */
export const toPolygonPoint = (point: unknown): PolygonPoint | null => {
  if (Array.isArray(point) && point.length >= 2 && !Array.isArray(point[0])) {
    const lat = asFinite(point[0]);
    const lng = asFinite(point[1]);
    return lat !== null && lng !== null ? [lat, lng] : null;
  }
  if (point && typeof point === 'object' && !Array.isArray(point)) {
    const rec = point as Record<string, unknown>;
    const lat = asFinite(rec.lat ?? rec.latitude);
    const lng = asFinite(rec.lng ?? rec.lon ?? rec.longitude);
    return lat !== null && lng !== null ? [lat, lng] : null;
  }
  return null;
};

const isCoordinatePair = (value: unknown): boolean => toPolygonPoint(value) !== null;

export const normalizePolygonPoints = (points: unknown): PolygonPoint[] => {
  if (typeof points === 'string') {
    try {
      return normalizePolygonPoints(JSON.parse(points));
    } catch {
      return [];
    }
  }
  if (!Array.isArray(points) || points.length === 0) return [];

  // Unwrap an extra GeoJSON-style ring: [[{lat,lng}, ...]] or [[[lat,lng], ...]].
  if (Array.isArray(points[0]) && !isCoordinatePair(points[0])) {
    return normalizePolygonPoints(points[0]);
  }

  return points.map(toPolygonPoint).filter((point): point is PolygonPoint => point !== null);
};

export const isValidPolygonPoint = (point: unknown): point is PolygonPoint =>
  Array.isArray(point) && point.length >= 2 && Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1]));

/** Ray-casting point-in-polygon test. `point` and `polygon` use `[lat, lng]`. */
export function isPointInPolygon(point: PolygonPoint, polygon: PolygonPoint[]): boolean {
  if (!isValidPolygonPoint(point)) return false;
  const ring = polygon.filter(isValidPolygonPoint);
  if (ring.length < 3) return false;
  const [lat, lng] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [latI, lngI] = ring[i];
    const [latJ, lngJ] = ring[j];
    const intersects = latI > lat !== latJ > lat && lng < ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Orientation of the ordered triplet (a, b, c). */
function orientation(a: PolygonPoint, b: PolygonPoint, c: PolygonPoint): number {
  const val = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  if (val === 0) return 0;
  return val > 0 ? 1 : 2;
}

/** True when point q lies on segment pr (assuming the three are collinear). */
function onSegment(p: PolygonPoint, q: PolygonPoint, r: PolygonPoint): boolean {
  return (
    Math.min(p[0], r[0]) <= q[0] &&
    q[0] <= Math.max(p[0], r[0]) &&
    Math.min(p[1], r[1]) <= q[1] &&
    q[1] <= Math.max(p[1], r[1])
  );
}

/** True when segment p1p2 intersects segment p3p4. */
export function segmentsIntersect(
  p1: PolygonPoint,
  p2: PolygonPoint,
  p3: PolygonPoint,
  p4: PolygonPoint
): boolean {
  const o1 = orientation(p1, p2, p3);
  const o2 = orientation(p1, p2, p4);
  const o3 = orientation(p3, p4, p1);
  const o4 = orientation(p3, p4, p2);

  if (o1 !== o2 && o3 !== o4) return true;

  if (o1 === 0 && onSegment(p1, p3, p2)) return true;
  if (o2 === 0 && onSegment(p1, p4, p2)) return true;
  if (o3 === 0 && onSegment(p3, p1, p4)) return true;
  if (o4 === 0 && onSegment(p3, p2, p4)) return true;

  return false;
}

/** Builds the list of edges for a path. When `closed`, the last vertex links back to the first. */
function buildEdges(points: PolygonPoint[], closed: boolean): [PolygonPoint, PolygonPoint][] {
  const edges: [PolygonPoint, PolygonPoint][] = [];
  for (let i = 0; i < points.length - 1; i++) edges.push([points[i], points[i + 1]]);
  if (closed && points.length >= 3) edges.push([points[points.length - 1], points[0]]);
  return edges;
}

/**
 * True when a drawn `path` overlaps a reference `polygon`. Handles three cases:
 * shared/crossing edges, the path lying inside the polygon, and (for closed
 * paths) the polygon lying inside the path.
 */
export function pathIntersectsPolygon(path: PolygonPoint[], polygon: PolygonPoint[], closed: boolean): boolean {
  const pathPoints = path.filter(isValidPolygonPoint);
  const polygonPoints = polygon.filter(isValidPolygonPoint);
  if (pathPoints.length === 0 || polygonPoints.length < 3) return false;

  if (pathPoints.some((pt) => isPointInPolygon(pt, polygonPoints))) return true;

  if (closed && pathPoints.length >= 3 && polygonPoints.some((pt) => isPointInPolygon(pt, pathPoints))) return true;

  const pathEdges = buildEdges(pathPoints, closed);
  const polygonEdges = buildEdges(polygonPoints, true);
  for (const [a, b] of pathEdges) {
    for (const [c, d] of polygonEdges) {
      if (segmentsIntersect(a, b, c, d)) return true;
    }
  }
  return false;
}

/** True when two closed polygons overlap in any way. */
export function polygonsIntersect(a: PolygonPoint[], b: PolygonPoint[]): boolean {
  return pathIntersectsPolygon(a, b, true);
}
